import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';

const LanguageContext = React.createContext();

const propTypes = {

	/**
	 * The preferred language for the current user.
	 */
	preferredLanguage: PropTypes.string.isRequired,

	/**
	 * The default language for the current scope (i.e. product|organization).
	 */
	defaultLanguage: PropTypes.string.isRequired,

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * The language context provider.
 *
 * @param {object} props  The shared context props.
 *
 * @returns {Object} result  The shared context state.
 * @returns {String} result.preferredLanguage  The preferred language for the current user.
 * @returns {String} result.defaultLanguage    The default language for the scope.
 * @returns {Func} result.chooseContentFromLanguage  Callback function to choose the translated
 *                                                   content to use for a multi-language object.
 */
const LanguageProvider = (props) => {

	const {
		preferredLanguage,
		defaultLanguage,
		children,
	} = props;

	/**
	 * Determines which multi-language content to show for an object given a
	 * user's preferred language setting and the default language for the scope.
	 *
	 * @param {Array} contents  The list of available multi-language contents for the object.
	 * @param {String} [defaultLanguageOverride]  An optional override value for the default language.
	 *
	 * @returns {Object} result  The content object to display.
	 */
	const chooseContentFromLanguage = useCallback((contents, defaultLanguageOverride) => {

		if (!contents || !Array.isArray(contents)) {
			return null;
		}

		const preferredContent = contents.find((content) => content.language === preferredLanguage);

		let defaultContent;

		// Check if overriding the default language for a specific piece of content
		if (defaultLanguageOverride) {

			defaultContent = contents.find((content) => content.language === defaultLanguageOverride);

		// Otherwise, use the default language for the scope
		} else {

			defaultContent = contents.find((content) => content.language === defaultLanguage);

		}

		return preferredContent || defaultContent;

	}, [preferredLanguage, defaultLanguage]);

	return (
		<LanguageContext.Provider
			value={{
				preferredLanguage,
				defaultLanguage,
				chooseContentFromLanguage,
			}}>
			{children}
		</LanguageContext.Provider>
	);

};

LanguageProvider.propTypes = propTypes;

/**
 * The language context hook.
 *
 * @returns {Object} The shared context state.
 */
const useLanguageContext = () => {

	const context = useContext(LanguageContext);

	if (!context) {
		throw new Error('useLanguageContext must be used within a LanguageProvider');
	}

	return context;

};

export {
	LanguageProvider,
	LanguageContext,
	useLanguageContext,
};
